import { useEffect, useRef } from 'react';

/**
 * Quando `active` è true, registra `onBack` come gestore del prossimo
 * back gesture / tasto Android. Più overlay aperti contemporaneamente
 * vengono gestiti come stack: il top viene chiuso per primo.
 *
 * Implementazione: stack globale di handler condiviso fra tutte le istanze;
 * un singolo listener `popstate` consuma il top. Ogni entry pushata in
 * window.history porta un id univoco così, alla chiusura via codice di un
 * overlay che NON è in cima (es. action sheet che apre un modal — il sheet
 * si chiude prima del modal), evitiamo di fare history.back consumando
 * l'entry sbagliata.
 */
type Handler = () => void;
const backStack: Handler[] = [];
let listenerInstalled = false;
let suppressOnce = false;

function installListener(): void {
  if (listenerInstalled) return;
  listenerInstalled = true;
  window.addEventListener('popstate', () => {
    if (suppressOnce) {
      suppressOnce = false;
      return;
    }
    const top = backStack.pop();
    if (top) top();
  });
}

let nextId = 1;

export function useBackGuard(active: boolean, onBack: () => void): void {
  const onBackRef = useRef(onBack);
  useEffect(() => {
    onBackRef.current = onBack;
  });

  useEffect(() => {
    installListener();
    if (!active) return;
    const id = nextId++;
    const handler: Handler = () => onBackRef.current();
    backStack.push(handler);
    window.history.pushState({ __backGuard: true, __id: id }, '');
    return () => {
      const idx = backStack.indexOf(handler);
      if (idx >= 0) backStack.splice(idx, 1);
      // Consuma l'entry sintetica solo se siamo ancora in cima alla history.
      // Se un altro overlay ha pushato dopo di noi, lasciamo l'entry zombie:
      // verrà consumata come no-op quando l'utente farà back più volte.
      const state = window.history.state as { __backGuard?: boolean; __id?: number } | null;
      if (state?.__backGuard && state.__id === id) {
        suppressOnce = true;
        window.history.back();
      }
    };
  }, [active]);
}
